import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Home, BarChart3, UserCog, FileText, Bell, ChevronDown, Users, TrendingUp, Award, Search, Mail, Download, MessageCircle, Calendar, Clock, ArrowRight, Zap, FileBarChart, Send, HelpCircle, CheckCircle, AlertCircle, Activity, Filter, ExternalLink, Building2, ClipboardList, Route as RouteIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { AIInsightPopover } from '../components/AIInsightPopover';
export function AdminHub() {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [emailToast, setEmailToast] = useState('');
  const [reminderLearner, setReminderLearner] = useState('');
  const [isReminderDropdownOpen, setIsReminderDropdownOpen] = useState(false);
  const [timePeriod, setTimePeriod] = useState<'7d' | '30d'>('30d');
  const userMenuRef = useRef<HTMLDivElement>(null);
  const reminderDropdownRef = useRef<HTMLDivElement>(null);
  const incompleteLearners = [{
    value: 'rachel-patel',
    name: 'Rachel Patel',
    email: 'rachel.patel@example.com'
  }, {
    value: 'david-nguyen',
    name: 'David Nguyen',
    email: 'd.nguyen@example.com'
  }, {
    value: 'karen-brooks',
    name: 'Karen Brooks',
    email: 'k.brooks@example.com'
  }, {
    value: 'marcus-hall',
    name: 'Marcus Hall',
    email: 'marcus.h@example.com'
  }, {
    value: 'tanya-foster',
    name: 'Tanya Foster',
    email: 'tanya.f@example.com'
  }];
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (reminderDropdownRef.current && !reminderDropdownRef.current.contains(event.target as Node)) {
        setIsReminderDropdownOpen(false);
      }
    };
    if (isUserMenuOpen || isReminderDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen, isReminderDropdownOpen]);
  const navigationCards = [{
    title: 'Analytics Dashboard',
    description: 'View detailed performance metrics and insights',
    icon: BarChart3,
    color: 'from-[#00638C] to-[#30718D]',
    stats: '248 learners tracked',
    link: '/analytics'
  }, {
    title: 'User Management',
    description: 'Manage learner accounts and permissions',
    icon: UserCog,
    color: 'from-[#30718D] to-[#265a70]',
    stats: '18 pending invites',
    link: '/assessment'
  }, {
    title: 'Reports',
    description: 'Generate and download custom reports',
    icon: FileText,
    color: 'from-[#007558] to-[#005a44]',
    stats: 'Last report: 1 hour ago',
    link: '/feedback'
  }, {
    title: 'Insights & Actions',
    description: 'Prioritize learner support with AI-powered skill insights',
    icon: Zap,
    color: 'from-[#DB3725] to-[#DB3725]',
    stats: '4 priority insights',
    link: '/insights-actions'
  }];
  const sendAccountCreationEmail = () => {
    const recipient = reminderLearner ? incompleteLearners.find(l => l.value === reminderLearner)?.name : null;
    setEmailToast(recipient ? `Account creation email sent to ${recipient}.` : 'Account creation email sent to all 5 incomplete learners.');
  };
  useEffect(() => {
    if (!emailToast) return;
    const timeoutId = window.setTimeout(() => setEmailToast(''), 4000);
    return () => window.clearTimeout(timeoutId);
  }, [emailToast]);
  return <div className="flex-1 bg-gray-50 min-h-screen" data-id="element-2169" data-mp="3a5ba8vpvktg6">
      {/* Email Sent Toast */}
      <AnimatePresence data-id="element-2170" data-mp="mx5aqh6pkpan">
        {emailToast && <motion.div role="status" aria-live="polite" initial={{
        opacity: 0,
        y: -50
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -50
      }} className="fixed top-20 left-1/2 z-50 flex max-w-[calc(100%-2rem)] -translate-x-1/2 items-center gap-3 rounded-lg bg-[#007558] px-6 py-3 text-white shadow-lg" data-id="element-2171" data-mp="hwpzpw9wftvu">
            <CheckCircle size={20} className="flex-shrink-0" data-id="element-2172" data-mp="10vlqsyqgns5g" />
            <span className="font-medium" data-id="element-2173" data-mp="10szigwvqx8m6">{emailToast}</span>
          </motion.div>}
      </AnimatePresence>

      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200" data-id="element-2174" data-mp="1alohute0uw15">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2175" data-mp="1okee66t0orog">
          <div className="flex items-center h-16" data-id="element-2176" data-mp="1oko7a39czeyd">
            {/* Logo */}
            <div className="text-[#E53E3E] font-bold text-xl" data-id="element-2177" data-mp="1jmmnfg43zblu">LOGO</div>

            {/* Navigation Links */}
            <nav className="flex items-center gap-4 sm:gap-8 ml-6 sm:ml-12" data-id="element-2178" data-mp="237mvzqgtzv9e">
              <Link data-phoebe-nav="/admin" to="/admin" className="flex items-center gap-2 text-[#00638C] border-b-2 border-[#00638C] pb-[22px] pt-[22px] transition-colors text-sm font-medium" data-id="element-2179" data-mp="28556hlahyiji">
                <Home size={18} data-id="element-2180" data-mp="1kglyz7z9u9sh" />
                <span className="hidden sm:inline" data-id="element-2181" data-mp="1kgc4p8771ggz">Admin Hub</span>
              </Link>
              <Link data-phoebe-nav="/analytics" to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2182" data-mp="1p7u9y8g9237r">
                <BarChart3 size={18} data-id="element-2183" data-mp="1fi0w10smxc0c" />
                <span className="hidden sm:inline" data-id="element-2184" data-mp="11hq4rwnexven">Analytics</span>
              </Link>
              <Link data-phoebe-nav="/feedback" to="/feedback" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2185" data-mp="11k3xvzyj8xjk">
                <FileText size={18} data-id="element-2186" data-mp="33es22oewplv8" />
                <span className="hidden sm:inline" data-id="element-2187" data-mp="33dpbyiperngd">Reports</span>
              </Link>
              <Link data-phoebe-nav="/assessment" to="/assessment" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2188" data-mp="2ygytxpu1mhsb">
                <UserCog size={18} data-id="element-2189" data-mp="3r2i7ie24btag" />
                <span className="hidden sm:inline" data-id="element-2190" data-mp="3m5i1u496ksjv">Users</span>
              </Link>
              <Link data-phoebe-nav="/survey" to="/survey" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2191" data-mp="3h6ewsjt7ambw">
                <ClipboardList size={18} data-id="element-2192" data-mp="25skxvfmilvje" />
                <span className="hidden sm:inline" data-id="element-2193" data-mp="25p6srzmgv889">Survey</span>
              </Link>
              <Link data-phoebe-nav="/pathway-builder" to="/pathway-builder" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2194" data-mp="21omyd3ta2ipt">
                <RouteIcon size={18} data-id="element-2195" data-mp="q8270i5kttmf" />
                <span className="hidden sm:inline" data-id="element-2196" data-mp="q70oxtq586v7">Pathway Builder</span>
              </Link>
            </nav>

            {/* Right Side - User Info */}
            <div className="flex items-center gap-2 sm:gap-4 ml-auto" data-id="element-2197" data-mp="zyws3j736n6a">
              <Link data-phoebe-nav="/alerts" to="/alerts" aria-label="Notifications" className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-2198" data-mp="100pweru88vb4">
                <Bell size={20} data-id="element-2199" data-mp="1dx3g87m8h8sr" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#DB3725] rounded-full" data-id="element-2200" data-mp="1dxd6lwb6xhyz"></span>
              </Link>
              <div className="relative" ref={userMenuRef} data-id="element-2201" data-mp="3assvyh9pynjb">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-2202" data-mp="3fqavjh1ujqen">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium" data-id="element-2203" data-mp="1hdzlthzjzjno">
                    LT
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-gray-900" data-id="element-2204" data-mp="1q8xgaticn4h4">
                    Linda Taylor
                  </span>
                  <ChevronDown size={16} className="text-gray-600" data-id="element-2205" data-mp="yhhl4b2q89fa" />
                </button>

                {isUserMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-id="element-2206" data-mp="1ci0p9742mvc6">
                    <div className="py-2" data-id="element-2207" data-mp="17gl5rjhjkwvw">
                      <Link to="/profile" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2208" data-mp="2nvmq3pjamldj">
                        <Users size={18} className="text-gray-600" data-id="element-2209" data-mp="4o8udoq8btww" />
                        <span className="font-medium text-gray-900" data-id="element-2210" data-mp="9o53u3hp4dju">
                          Profile
                        </span>
                      </Link>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2211" data-mp="3rvujzxtrhn78">
                        <Home size={18} className="text-gray-600" data-id="element-2212" data-mp="ocrf9u47cl5s" />
                        <span className="font-medium text-gray-900" data-id="element-2213" data-mp="ofdvsj92mb05">
                          Learner Hub
                        </span>
                      </button>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2214" data-mp="emolvst6556x">
                        <UserCog size={18} className="text-gray-600" data-id="element-2215" data-mp="jk85l88mya69" />
                        <span className="font-medium text-gray-900" data-id="element-2216" data-mp="17aua4pjflcbi">
                          Admin Hub
                        </span>
                      </button>
                      <div className="border-t border-gray-200 my-2" data-id="element-2217" data-mp="q0sc0hz3y1sp"></div>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2218" data-mp="uzwll9py21jk">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-2219" data-mp="gzlwab4a8v0i">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" data-id="element-2220" data-mp="2b7v04milem2"></path>
                          <polyline points="16 17 21 12 16 7" data-id="element-2221" data-mp="294tanc01x1i"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12" data-id="element-2222" data-mp="3khm49q9u90e4"></line>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-2223" data-mp="3pfnmk8zfwvoj">
                          Logout
                        </span>
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-2224" data-mp="3athyr648wanq">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-end justify-between gap-4" data-id="element-2225" data-mp="3avunc24idj9r">
          <div data-id="element-2226" data-mp="3oqwroktpj0kg">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#30718D] mb-1" data-id="element-2227" data-mp="3jug53v25ou9h">
              Administrator
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-1" data-id="element-2228" data-mp="2cdg0vw49zho">
              Welcome Back, Linda
            </h1>
          </div>
          <button className="text-[#DB3725] hover:text-[#c02f1f] font-medium text-sm transition-colors self-start sm:self-auto flex items-center gap-1.5" data-id="element-2229" data-mp="3tm2ls6x9jljm">
            <Mail size={14} data-id="element-2230" data-mp="6gbd711vt083" />
            Contact support
          </button>
        </div>

        {/* Company & Send Email Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8" data-id="element-2231" data-mp="3055da2z9ectf">
          {/* Company Info Card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.05
        }} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 flex flex-col" data-phoebe-tour="company-info" data-id="element-2232" data-mp="3548gdhvoi8yr">
            <div className="flex items-center gap-3 mb-4" data-id="element-2233" data-mp="3nyngjatq87o6">
              <div className="w-8 h-8 rounded-lg bg-[#30718D]/10 flex items-center justify-center flex-shrink-0" data-id="element-2234" data-mp="3sx8hjcgsyvdo">
                <Building2 size={16} className="text-[#30718D]" data-id="element-2235" data-mp="rbphdpb1tv5" />
              </div>
              <div data-id="element-2236" data-mp="1yjh2tyjm9lbd">
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-2237" data-mp="1yjz8tmw2zdwh">
                  Company Name
                </div>
                <div className="text-base font-semibold text-gray-900" data-id="element-2238" data-mp="1v08hmho0hc6k">
                  Lorem Ipsum
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-100" data-id="element-2239" data-mp="1q7fc3rkpxztf">
              <div className="flex items-center gap-3" data-id="element-2240" data-mp="1layr368tg1kk">
                <div className="w-8 h-8 rounded-lg bg-[#007558]/10 flex items-center justify-center flex-shrink-0" data-id="element-2241" data-mp="1h8u0kgdnzl25">
                  <Users size={16} className="text-[#007558]" data-id="element-2242" data-mp="2dz2x5svaugns" />
                </div>
                <div data-id="element-2243" data-mp="28zzugf2skmiz">
                  <div className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-2244" data-mp="290t17g1r3c14">
                    Registered Learners
                  </div>
                  <div className="text-base font-semibold text-gray-900" data-id="element-2245" data-mp="1zz1vwcb4h77s">
                    4 Learners
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-3" data-id="element-2246" data-mp="tgmwev1p69s6">
              <div className="flex items-center gap-3" data-id="element-2247" data-mp="ojd7dgi8004v">
                <div className="w-8 h-8 rounded-lg bg-[#DB3725]/10 flex items-center justify-center flex-shrink-0" data-id="element-2248" data-mp="olzi1p0eqvrb">
                  <Clock size={16} className="text-[#DB3725]" data-id="element-2249" data-mp="2h7aza2mk7uxu" />
                </div>
                <div data-id="element-2250" data-mp="2h95kzj5nnr2z">
                  <div className="text-xs font-semibold uppercase tracking-wider text-gray-500" data-id="element-2251" data-mp="23g67fmpfo6y2">
                    Pending
                  </div>
                  <div className="text-base font-semibold text-gray-900" data-id="element-2252" data-mp="27i142dwf5w5i">
                    5 Learners
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Send Account Creation Email */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 lg:col-span-2" data-phoebe-tour="send-email" data-id="element-2253" data-mp="2m6xbq9ukd75l">
            <div className="flex items-center gap-3 mb-4" data-id="element-2254" data-mp="178rxl7znloo5">
              <div className="w-10 h-10 rounded-lg bg-[#30718D]/10 flex items-center justify-center" data-id="element-2255" data-mp="17be3z4at5y4s">
                <Mail size={20} className="text-[#30718D]" data-id="element-2256" data-mp="13a2d6vg776v0" />
              </div>
              <div data-id="element-2257" data-mp="1h6pm7wlfdnha">
                <h3 className="text-lg font-bold text-gray-900" data-id="element-2258" data-mp="1h54s2830w6jy">
                  Send Account Creation Email
                </h3>
                <p className="text-sm text-gray-600" data-id="element-2259" data-mp="oia77a2an55y">
                  Remind learners to complete account setup.{' '}
                  <span className="font-semibold text-[#DB3725]" data-id="element-2260" data-mp="jl1q7csvt76a">
                    5 pending
                  </span>
                </p>
              </div>
            </div>

            <div className="flex gap-3 items-end" data-id="element-2261" data-mp="them8hbwfx47">
              <div className="relative flex-1" ref={reminderDropdownRef} data-id="element-2262" data-mp="tftn0een3q00">
                <button onClick={() => {
                setIsReminderDropdownOpen(!isReminderDropdownOpen);
              }} className="flex items-center gap-2 w-full px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-white text-sm font-medium" data-id="element-2263" data-mp="5r0xazom2edu">
                  <Users size={18} className="text-[#30718D]" data-id="element-2264" data-mp="2ala5aa822zqq" />
                  <span className="flex-1 text-left text-gray-700" data-id="element-2265" data-mp="2altn7qxpho4r">
                    {reminderLearner ? incompleteLearners.find(l => l.value === reminderLearner)?.name : 'All incomplete learners (5)'}
                  </span>
                  <ChevronDown size={16} className={`text-gray-400 transition-transform ${isReminderDropdownOpen ? 'rotate-180' : ''}`} data-id="element-2266" data-mp="1mxk632bftojy" />
                </button>

                {isReminderDropdownOpen && <div className="absolute top-full mt-2 left-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-80 overflow-y-auto" data-id="element-2267" data-mp="32by4hgq65v1r">
                    <button onClick={() => {
                  setReminderLearner('');
                  setIsReminderDropdownOpen(false);
                }} className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 text-sm ${reminderLearner === '' ? 'bg-[#30718D]/5 text-[#30718D] font-semibold' : 'text-gray-700'}`} data-id="element-2268" data-mp="3hg0weg7qqmcs">
                      All incomplete learners
                    </button>
                    {incompleteLearners.map(learner => <button key={learner.value} onClick={() => {
                  setReminderLearner(learner.value);
                  setIsReminderDropdownOpen(false);
                }} className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 ${reminderLearner === learner.value ? 'bg-[#30718D]/5' : ''}`} data-id="element-2269" data-mp="2ts9hr5yq9x8c">
                        <div className={`font-medium text-sm ${reminderLearner === learner.value ? 'text-[#30718D]' : 'text-gray-900'}`} data-id="element-2270" data-mp="128q1ox90fn7m">
                          {learner.name}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-2271" data-mp="xevehju74q3s">
                          {learner.email}
                        </div>
                      </button>)}
                  </div>}
              </div>
              <button onClick={sendAccountCreationEmail} className="px-4 py-2.5 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors flex items-center gap-2 text-sm font-medium" data-id="element-2272" data-mp="nfcwh8xujeed">
                <Send size={16} data-id="element-2273" data-mp="23x0nlcenzed6" />
                Send
              </button>
            </div>
          </motion.div>
        </div>

        {/* Performance Snapshot */}
        <div data-phoebe-tour="dashboard" className="mb-8" data-id="element-2274" data-mp="2iyqxxzue3xbo">
          <div className="flex items-center gap-2 mb-4" data-id="element-2275" data-mp="2iz8pwka0rpds">
            <h2 className="text-xl font-bold text-gray-900" data-id="element-2276" data-mp="2e0p4ckzc8fly">
              Performance Snapshot
            </h2>
            <span data-phoebe-tour="ai-insight-icon" className="inline-flex" data-id="element-2277" data-mp="2rscardmzv4ig">
            <AIInsightPopover align="left" headline="Participation is strong but completion is slipping" insightBeforeMetric="80% of learners were active this month, yet only" highlightedMetric="10%" insightAfterMetric=" have finished every module." explanation="The opportunity sits with active learners who have one or two modules left — a focused reminder should close the gap." insightId={12} data-id="element-2278" data-mp="2n02e7erdj677" />
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2279" data-mp="1gcfcgyt9n88k">
            {/* Card 1 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col" data-id="element-2280" data-mp="1cc4x4vjl35h1">
              <div className="p-6 flex-1 flex flex-col" data-id="element-2281" data-mp="og3omquiiruf">
                <div className="mb-3 text-gray-700 leading-relaxed" data-id="element-2282" data-mp="oxy9hqy4h5v">
                  <span className="text-4xl font-bold text-gray-900 mr-2 align-middle" data-id="element-2283" data-mp="3rw0puaap4pgm">
                    55
                  </span>
                  <span className="align-middle text-sm" data-id="element-2284" data-mp="9qny9byw8of9">
                    of your invited learners have
                    <br data-id="element-2285" data-mp="5nrgfnh6gwcd" />
                    completed registration
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-[#007558] text-sm font-medium" data-id="element-2286" data-mp="1l3zy7brj8mw5">
                  <span className="text-xs" data-id="element-2287" data-mp="1h3g0o731rh9h">▲</span>
                  <span data-id="element-2288" data-mp="1onry80hhr4ar">16% vs 30 days ago</span>
                </div>
              </div>
              <div className="h-1.5 w-full bg-[#007558]" data-id="element-2289" data-mp="1tmlge67q3jh9"></div>
            </motion.div>

            {/* Card 2 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-visible flex flex-col" data-id="element-2290" data-mp="1epucpkr0nllu">
              <div className="p-6 flex-1 flex flex-col" data-id="element-2291" data-mp="1xoq30kzr7fue">
                <div className="flex items-start justify-between mb-3 gap-2" data-id="element-2292" data-mp="1xm3yyvc2nigx">
                  <div className="text-gray-700 leading-relaxed" data-id="element-2293" data-mp="22loz392z9wv8">
                    <span className="text-4xl font-bold text-gray-900 mr-2 align-middle" data-id="element-2294" data-mp="2qcly5l9oczp2">
                      {timePeriod === '7d' ? '65%' : '80%'}
                    </span>
                    <span className="align-middle text-sm" data-id="element-2295" data-mp="34zaytqfnxix1">
                      of your learners have
                      <br data-id="element-2296" data-mp="300pyzs4bp194" />
                      spent time learning this{' '}
                      {timePeriod === '7d' ? 'week' : 'month'}
                    </span>
                  </div>
                  <div className="flex items-start gap-2 flex-shrink-0" data-id="element-2297" data-mp="2r01t5kp3277b">
                    <div className="flex items-center bg-gray-100 rounded-md p-px" data-id="element-2298" data-mp="2r1w3k52qagx5">
                      <button onClick={() => setTimePeriod('7d')} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold transition-all whitespace-nowrap leading-tight ${timePeriod === '7d' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`} data-id="element-2299" data-mp="2m1zys3m7ilgj">
                        7 Days
                      </button>
                      <button onClick={() => setTimePeriod('30d')} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold transition-all whitespace-nowrap leading-tight ${timePeriod === '30d' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`} data-id="element-2300" data-mp="23ca5jky3e7qq">
                        30 Days
                      </button>
                    </div>
                  </div>
                </div>
                <div className={`flex items-center gap-1.5 text-sm font-medium ${timePeriod === '7d' ? 'text-[#007558]' : 'text-[#007558]'}`} data-id="element-2301" data-mp="rsba9mm2x1ds">
                  <span className="text-xs" data-id="element-2302" data-mp="2qcvj2ttsxg">▲</span>
                  <span data-id="element-2303" data-mp="3w4ovb8g2uijq">
                    {timePeriod === '7d' ? '12% vs prior 7 days' : '20% vs 30 days ago'}
                  </span>
                </div>
              </div>
              <div className="h-1.5 w-full bg-[#007558]" data-id="element-2304" data-mp="38hqmmwy78ewd"></div>
            </motion.div>

            {/* Card 3 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-visible flex flex-col" data-id="element-2305" data-mp="3ciaebl006qb4">
              <div className="p-6 flex-1 flex flex-col" data-id="element-2306" data-mp="2ycijrh56u8dw">
                <div className="mb-3 text-gray-700 leading-relaxed" data-id="element-2307" data-mp="3c4yfdwvrf44s">
                  <span className="text-4xl font-bold text-gray-900 mr-2 align-middle" data-id="element-2308" data-mp="3c33u2hgkta06">
                    10%
                  </span>
                  <span className="align-middle text-sm" data-id="element-2309" data-mp="3m2uoyk91tr0s">
                    of your learners have
                    <br data-id="element-2310" data-mp="3h4b28hmmsb7d" />
                    completed all skill modules
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-[#DB3725] text-sm font-medium" data-id="element-2311" data-mp="1robmuisf5pja">
                  <span className="text-xs" data-id="element-2312" data-mp="1mqjytzuwankd">▼</span>
                  <span data-id="element-2313" data-mp="1ms3d5kwzqoji">20% vs 30 days ago</span>
                </div>
              </div>
              <div className="h-1.5 w-full bg-[#DB3725]" data-id="element-2314" data-mp="28rhdjeu03xz"></div>
            </motion.div>
          </div>
        </div>

        {/* Navigation Cards */}
        <div data-phoebe-tour="navigation-cards" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-2315" data-mp="3oo2fnto1jsew">
          {navigationCards.map((card, index) => {
          const Icon = card.icon;
          return <Link key={index} to={card.link} className="h-full" data-id="element-2316" data-mp="3fm2zt4oriudj" data-mpi={index}>
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.5 + index * 0.1
            }} className="group flex h-full flex-col bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all cursor-pointer" data-id="element-2317" data-mp="3joza8845fk6e">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`} data-id="element-2318" data-mp="17gagot9umzxs">
                    <Icon size={24} className="text-white" data-id="element-2319" data-mp="xhlc226yrbzh" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#00638C] transition-colors" data-id="element-2320" data-mp="sj1ndu0zjm3f">
                    {card.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4 flex-1" data-id="element-2321" data-mp="1l6xf0kbp6ntc">
                    {card.description}
                  </p>
                  <div className="mt-auto flex items-center justify-between gap-2" data-id="element-2322" data-mp="1gew0ssuyyn0o">
                    <span className="text-xs font-medium text-gray-500" data-id="element-2323" data-mp="1bhmalazra7bs">
                      {card.stats}
                    </span>
                    <ArrowRight size={16} className="text-gray-400 group-hover:text-[#00638C] group-hover:translate-x-1 transition-all" data-id="element-2324" data-mp="28xjevobjt1s9" />
                  </div>
                </motion.div>
              </Link>;
        })}
        </div>

        {/* Contact Support - Full Width Horizontal */}
        <motion.div data-phoebe-tour="support" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.8
      }} className="bg-gradient-to-br from-[#30718D] to-[#265a70] rounded-xl p-6 shadow-lg text-white mb-8" data-id="element-2325" data-mp="2leknyxqxxqw2">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6" data-id="element-2326" data-mp="34524m3yi0w12">
            <div className="flex items-center gap-4" data-id="element-2327" data-mp="394ojehtz6kbz">
              <div className="w-12 h-12 rounded-lg bg-white/15 backdrop-blur-sm flex items-center justify-center flex-shrink-0" data-id="element-2328" data-mp="3969ec8k7c5ad">
                <HelpCircle size={24} className="text-white" data-id="element-2329" data-mp="3j2308ijt9b4t" />
              </div>
              <div data-id="element-2330" data-mp="8858tto5f6sm">
                <h3 className="text-xl font-bold mb-1" data-id="element-2331" data-mp="87ndr0d82yma">Need Help?</h3>
                <p className="text-sm text-white/70 max-w-2xl" data-id="element-2332" data-mp="392fv7lg0r10">
                  Our support team is available 24/7 to help you with any
                  questions or issues you may have.
                </p>
              </div>
            </div>
            <div className="flex flex-col items-center md:items-end justify-center gap-3 w-full md:w-auto" data-id="element-2333" data-mp="catlkcfzgxuv">
              <button className="w-full md:w-auto px-6 py-3 bg-white text-[#30718D] rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 text-sm font-semibold shadow-sm" data-id="element-2334" data-mp="3kqdax0dj9w4n">
                <Mail size={16} data-id="element-2335" data-mp="3ugy60du00iit" />
                Email Support
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>;
}